import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu, LogOut, User, Volleyball, BookText, ImageIcon, Users, Award, LineChart, CreditCard } from "lucide-react";
import logoImage from "@assets/legacyloop-white-trans.png";
import { AthleteSwitcher } from "@/components/AthleteSwitcher";
import appStoreBadge from "@assets/download-on-app-store.svg";

const Header = () => {
  const { user, isAuthenticated, logout, isLoggingOut } = useAuth();
  const [, setLocation] = useLocation();

  const navItems = [
    { title: "Dashboard", href: "/dashboard", icon: <Volleyball className="h-5 w-5" /> },
    { title: "Journal", href: "/journal", icon: <BookText className="h-5 w-5" /> },
    { title: "Media Library", href: "/media", icon: <ImageIcon className="h-5 w-5" /> },
    { title: "In the Loop", href: "/family", icon: <Users className="h-5 w-5" /> },
    { title: "Stats", href: "/stats", icon: <LineChart className="h-5 w-5" /> },
    { title: "Recruiting", href: "/recruiting", icon: <Award className="h-5 w-5" /> },
    { title: "Subscription", href: "/subscription", icon: <CreditCard className="h-5 w-5" /> },
  ];

  return (
    <header className="bg-[#0D1B2A]/90 backdrop-blur-sm border-b border-[#1B263B] text-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <img
            src={logoImage}
            alt="LegacyLoop"
            className="h-12 w-auto"
          />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          {navItems.map((item) => (
            <Link key={item.href} href={item.href}>
              <span className="hover:text-[#E0A458] transition duration-200 font-medium text-sm cursor-pointer">
                {item.title}
              </span>
            </Link>
          ))}
        </div>

        <div className="flex items-center space-x-3">
          <AthleteSwitcher />
          {/* Mobile Menu Button */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-white">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 bg-[#0D1B2A] text-white border-[#1B263B]">
              <SheetHeader>
                <SheetTitle className="text-white flex items-center space-x-2">
                  <img
                    src={logoImage}
                    alt="LegacyLoop"
                    className="h-6 w-auto"
                  />
                </SheetTitle>
              </SheetHeader>
              <nav className="space-y-2 mt-8">
                {navItems.map((item) => (
                  <SheetClose asChild key={item.href}>
                    <Link href={item.href}>
                      <div className="flex items-center space-x-2 px-2 py-2 rounded-md hover:bg-[#1B263B] transition-colors cursor-pointer">
                        {item.icon}
                        <span>{item.title}</span>
                      </div>
                    </Link>
                  </SheetClose>
                ))}
              </nav>

              <a
                href="https://apps.apple.com/app/id6795783764"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download Legacy Loop on the App Store"
                className="mt-6 inline-block px-2 transition-transform hover:scale-105"
              >
                <img src={appStoreBadge} alt="Download on the App Store" className="h-11 w-auto" />
              </a>

              {isAuthenticated && (
                <div className="mt-6 pt-4 border-t border-[#E0A458]/30">
                  <SheetClose asChild>
                    <Button
                      variant="ghost"
                      onClick={logout}
                      disabled={isLoggingOut}
                      className="w-full justify-start text-white hover:bg-[#1B263B] hover:text-white"
                    >
                      <LogOut className="mr-2 h-5 w-5" />
                      {isLoggingOut ? "Logging out..." : "Log out"}
                    </Button>
                  </SheetClose>
                </div>
              )}
            </SheetContent>
          </Sheet>

          {/* User Profile */}
          {isAuthenticated ? (
            <div className="hidden md:block relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-1 p-1 hover:bg-[#1B263B]">
                    <Avatar className="h-8 w-8 border-2 border-[#E0A458]">
                      <AvatarImage src={(user as any)?.profileImageUrl} alt={(user as any)?.firstName || "User"} />
                      <AvatarFallback className="bg-[#415A77] text-white font-medium">
                        {(user as any)?.firstName?.[0]?.toUpperCase() || (user as any)?.email?.[0]?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border-[#E0A458]/40 bg-[#111827] text-slate-100 shadow-xl">
                  <DropdownMenuLabel className="text-slate-100 font-semibold">My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#415A77]/60" />
                  <DropdownMenuItem onClick={() => setLocation("/profile")} className="text-slate-100 hover:bg-[#1B263B]">
                    <User className="mr-2 h-4 w-4 text-[#E0A458]" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#415A77]/60" />
                  <DropdownMenuItem onClick={logout} disabled={isLoggingOut} className="text-slate-100 hover:bg-[#1B263B]">
                    <LogOut className="mr-2 h-4 w-4 text-[#E0A458]" />
                    <span>{isLoggingOut ? "Logging out..." : "Log out"}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="bg-[#E0A458] text-black hover:bg-[#f0b467] border-0"
              onClick={() => window.location.href = "/api/login"}
            >
              Log in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
